import { z } from 'zod';

/**
 * One sample emitted by `flex-bridge` over its bi-directional control
 * channel. The bridge keeps a rolling in-memory history on the box; samples
 * only flow to Reflex while at least one viewer is watching the monitoring
 * panel (viewer-gated `metrics.subscribe`).
 *
 * Capacity fields (`cpuCores`, `memTotalBytes`, `diskTotalBytes`) are the
 * box's discovered effective limits from cgroups (with `/proc` fallbacks for
 * unlimited / non-containerized hosts). The UI uses them to plot absolute
 * numbers and to label each chart with the limit the percent threshold is
 * computed against, so the on-box detection and the UI threshold line
 * always agree on what 100% means.
 */
export const SandboxMetricSampleSchema = z.object({
  timestamp: z.number(),
  cpuPct: z.number().min(0).max(100),
  cpuCores: z.number().positive(),
  memUsedBytes: z.number().nonnegative(),
  memTotalBytes: z.number().positive(),
  netRxBps: z.number().nonnegative(),
  netTxBps: z.number().nonnegative(),
  diskUsedBytes: z.number().nonnegative(),
  diskTotalBytes: z.number().positive(),
  diskUsedPct: z.number().min(0).max(100),
  diskReadBps: z.number().nonnegative(),
  diskWriteBps: z.number().nonnegative(),
});
export type SandboxMetricSample = z.infer<typeof SandboxMetricSampleSchema>;

/**
 * Threshold configuration the bridge applies (and the UI plots as a
 * recharts `ReferenceLine` on the matching graph). Reflex passes these to
 * the bridge at start, the bridge echoes them back in its `hello` message,
 * and the UI reads them from each `sandbox:metrics` push - so the line on
 * the chart matches what actually trips the warning.
 */
export const SandboxThresholdsSchema = z.object({
  cpuPct: z.number().min(0).max(100),
  memPct: z.number().min(0).max(100),
  /** Sustained-high window in seconds (e.g. 60s of >=9 of last 12 samples). */
  windowSeconds: z.number().positive(),
});
export type SandboxThresholds = z.infer<typeof SandboxThresholdsSchema>;

/**
 * Per-metric warning emitted by the bridge when its on-box sustained-high
 * detection trips. Reflex forwards the warning to the agent's axon stream
 * as `agent.performance_warning`; the UI renders a warning bubble in chat
 * and (when the monitoring panel is open) the live graph shows the value
 * crossing the threshold reference line.
 */
export const SandboxWarningMetricSchema = z.enum(['cpu', 'memory']);
export type SandboxWarningMetric = z.infer<typeof SandboxWarningMetricSchema>;

/**
 * One process in the box's `top`-style breakdown. The bridge enumerates the
 * box's PID namespace each tick and reports a curated top-N (by CPU or memory)
 * so the UI can show who is actually using the resource without dumping every
 * process. `cpuPct` is normalized to the box's effective cores (so it shares
 * the 0-100 scale with the box CPU graph and the per-process values roughly sum
 * to it). `memBytes` is resident set size (RSS), matching `top`'s RES column.
 */
export const SandboxProcessSampleSchema = z.object({
  pid: z.number().int().nonnegative(),
  /** Short process name (from `/proc/<pid>/comm`). */
  name: z.string(),
  /** CPU usage as a percentage of the box's effective cores (0-100). */
  cpuPct: z.number().min(0).max(100),
  /** Resident memory (bytes). */
  memBytes: z.number().nonnegative(),
  /** Full command line, when available. Shown on hover / when expanded. */
  command: z.string().optional(),
});
export type SandboxProcessSample = z.infer<typeof SandboxProcessSampleSchema>;

export const SandboxWarningPayloadSchema = z.object({
  metric: SandboxWarningMetricSchema,
  /** Latest sampled value (percent for cpu/memory). */
  value: z.number(),
  /** The threshold the value crossed (same units as `value`). */
  threshold: z.number(),
  /** The discovered effective limit (cores for cpu, bytes for memory). */
  limit: z.number(),
  /** Sustained-high window in seconds the detection used. */
  windowSeconds: z.number().positive(),
  /** Human-readable summary (e.g. "CPU sustained above 85% for 60s"). */
  message: z.string(),
  /**
   * The metric's value across the detection window (oldest -> newest, percent
   * for cpu/memory). Embedded so the chat warning can draw a static mini-graph
   * of the offending period, viewable long after the live metrics stream has
   * stopped. Defaults to empty for back-compat with older bridges.
   */
  series: z.array(z.number()).default([]),
  /**
   * Snapshot of the top processes (by the offending metric) at the moment the
   * warning tripped. Embedded so the chat warning can show who was using the
   * resource even after the box is gone. Defaults to empty for back-compat.
   */
  processes: z.array(SandboxProcessSampleSchema).default([]),
});
export type SandboxWarningPayload = z.infer<typeof SandboxWarningPayloadSchema>;

/**
 * Notify kinds the host itself handles, mirroring each to a core axon event:
 * - `daemon` -> `agent.daemon_started` (carries `name`/`port`/`info`).
 * - `pr` -> `agent.pr_created` (carries the pull-request fields below; `name`
 *   is the PR title). Lets `reflex-service pr` link a PR to the agent.
 *
 * Both are core agent concepts (a running service, a linked pull request), so
 * the host owns them end to end.
 */
export const SANDBOX_NOTIFY_HOST_KINDS = ['daemon', 'pr'] as const;
export type SandboxNotifyHostKind = (typeof SANDBOX_NOTIFY_HOST_KINDS)[number];

/**
 * Notify payload pushed by the bridge whenever the on-box CLI (or another
 * local agent surface) posts to its localhost IPC endpoint.
 *
 * `kind` is an open string, not an enum. Beyond the two host kinds above, a
 * plugin claims its own kind with
 * `sandboxBridgeService.registerNotifyHandler(kind, handler)` and ships the
 * on-box subcommand that posts it — so adding a new on-box-to-chat capability
 * needs no change to this schema, to the bridge, or to the host service.
 * Anything the host does not recognize and no plugin has claimed is dropped
 * (and acked, so it drains off the box) rather than retried forever.
 */
export const SandboxNotifyKindSchema = z.string().min(1);
export type SandboxNotifyKind = z.infer<typeof SandboxNotifyKindSchema>;

/**
 * Delivery guarantee the bridge applies when relaying a notify to Reflex.
 * - `async`: durable, at-least-once. The caller does not block, so the bridge
 *   persists the message on the box, reloads it on restart, and redelivers it on
 *   every reconnect until Reflex acks it (`notify.ack`) — it survives a bridge
 *   restart / devbox suspend-resume. This is the default for fire-and-forget
 *   side effects the agent does not wait on: both the PR link and a started
 *   daemon, which the bridge must not silently drop.
 * - `sync`: blocking, in-memory. The on-box CLI (and the agent tool call driving
 *   it) blocks until the bridge confirms delivery, so nothing restarts under an
 *   in-flight notify and there is nothing to persist. Reserved for operations
 *   that must complete before the agent can continue; no current caller (all of
 *   today's notifies are async).
 *
 * Optional on the wire so an older bridge that sends neither field still
 * validates; a missing value is treated as `async` (durable), so a notify from
 * an older CLI can't be silently dropped.
 */
export const SandboxNotifyDeliverySchema = z.enum(['sync', 'async']);
export type SandboxNotifyDelivery = z.infer<typeof SandboxNotifyDeliverySchema>;

export const SandboxNotifyPayloadSchema = z.object({
  kind: SandboxNotifyKindSchema,
  name: z.string(),
  port: z.number().int().positive().optional(),
  info: z.string().optional(),
  url: z.string().optional(),
  number: z.number().int().positive().optional(),
  repo: z.string().optional(),
  branch: z.string().optional(),
  /**
   * PR association role from `reflex-service pr --role`. Kept a plain string
   * on the wire (an unknown value from a newer/older CLI must not invalidate
   * the whole notify); the consumer narrows it to `creator` / `reviewer` and
   * treats anything else as `creator`.
   */
  role: z.string().optional(),
  /**
   * Whether the PR being registered is a draft, from `gh pr view --json
   * isDraft`. Optional: an older on-box CLI omits it, and the host then leaves
   * the association state unrecorded (which reads as `open`).
   */
  draft: z.boolean().optional(),
  /**
   * Free-form payload for a plugin-owned notify kind. Opaque to the on-box
   * CLI, the bridge, and the host: all three relay it verbatim to whichever
   * plugin claimed the kind, which parses it at its own boundary.
   *
   * This is what keeps the notify path extensible without schema churn — a
   * plugin adding an on-box capability puts its whole payload here instead of
   * growing new named fields on this shared type (as the assessment document
   * briefly did).
   */
  data: z.unknown().optional(),
  /**
   * Bridge-assigned message id. Present on `sync` notifies (and any notify a
   * current bridge relays) so Reflex can ack it and dedupe redeliveries.
   * Optional for back-compat with an older bridge that didn't stamp one.
   */
  id: z.string().optional(),
  /** Delivery guarantee; absent means `async`. See {@link SandboxNotifyDeliverySchema}. */
  delivery: SandboxNotifyDeliverySchema.optional(),
});
export type SandboxNotifyPayload = z.infer<typeof SandboxNotifyPayloadSchema>;
